/**
 * The state machine behind the upload-required modal's "a NATIVE backup upload
 * is still running" gate (DSK-879).
 *
 * ## Why this is a machine and not a `string | null`
 *
 * The first version of this was one atom holding a session id: the stop raised
 * it on the `backupPendingAsync` branch, and `useSelfHealingStopEvents` lowered
 * it when the durable recovery row settled. A raise whose ONLY clearer is a
 * different actor, reached over a channel that can be switched off, is a
 * promise the raiser cannot keep — and both ways of breaking it land on the
 * exact screen DSK-879 named: a full-viewport overlay, a progress bar, and no
 * actions, for the life of the tab.
 *
 *  1. **Arrival order.** The raise runs several `await`s after the fact it
 *     records becomes true (the duration PATCH, and a typed-outcome recovery on
 *     a timed-out stop). A settle that lands inside that window found a `null`
 *     atom, cleared nothing, and was then overwritten by the raise. Nothing
 *     re-emits it.
 *  2. **The channel.** `release-apps-self-healing-stop-desktop` does not merely
 *     disarm the FE listener, it disarms the PRODUCER:
 *     `onlineBackupArmReconcile({ enabled: false })` calls `store.set_armed`,
 *     after which no reconcile pass starts
 *     (`online_backup/reconcile.rs:67`), no in-process retry is scheduled
 *     (`:187`), and a retry timer that was already pending fires, re-checks
 *     `is_armed()` and bails (`:251`). While the switch is off, nothing is even
 *     attempting the work that would move a row to `uploaded` /
 *     `terminalEvicted` / `abandonedRetained`. The settle is not un-heard, it
 *     is un-sent — so no listener, however placed, can rescue it.
 *
 * ## The guarantee
 *
 * A gate can only be RAISED into an entry the same stop opened BEFORE its own
 * `stopSession` IPC, and only while the settle channel is armed. Because the
 * watch is opened before the native upload can exist, every settle for that
 * upload is observed against an existing entry, so the two interleavings reach
 * the *identical* state:
 *
 *   open → settle → raise   ⇒ `settled`, raise refused    ⇒ gate DOWN
 *   open → raise  → settle  ⇒ `raised`, then removed      ⇒ gate DOWN
 *
 * and disarming the channel drops every entry it could no longer clear. The
 * raised state is therefore a strict subset of "this stop's upload is live AND
 * something can still say otherwise" — there is no interleaving left in which
 * the gate survives its session.
 *
 * ## Why this does not overshoot into never raising
 *
 * A raise is refused in exactly two situations, and in both of them raising is
 * the worse answer: the upload has ALREADY settled (nothing is uploading, so
 * the recovery actions are correct), or nothing can report a settle (the gate
 * would be permanent, and the actions are the only way out). On the path this
 * gate exists for — kill switch on, listener mounted, upload genuinely in
 * flight — the raise succeeds exactly as before. Note also that a client that
 * cannot arm the channel cannot produce `backupPendingAsync` either: every
 * producer of it needs the `backup_recovery` store open and the self-healing
 * config set, and `online_backup_arm_reconcile` is the only writer of both.
 *
 * Pure and dependency-free on purpose: the guarantee is these five reducers, and
 * a test that has to mock jotai to reach a hook still runs them for real.
 */

/**
 * `watching` — a stop is in flight for this session and no settle has been seen
 * since it opened. Inert: it gates nothing, it only makes a raise POSSIBLE.
 *
 * `settled` — a settle arrived while watching. Terminal for this watch: the
 * raise that is still on its way must be refused.
 *
 * `raised` — the gate is up. The only state the modal reads, and the only one
 * reachable through {@link raiseNativeBackupUploadGate}.
 */
export type NativeBackupUploadWatch = 'watching' | 'settled' | 'raised';

export type NativeBackupUploadGate = {
  /**
   * Whether the durable-row settle channel is armed: the FE is on desktop, the
   * kill switch is on, and the user is authenticated — i.e. exactly the state in
   * which `online_backup_arm_reconcile` has been told `enabled: true`. ANDed
   * into every read, so a disarm cannot be outlived even by a raise that got in
   * first.
   */
  readonly channelArmed: boolean;
  /** Session id → watch state. Bounded by the number of live stops. */
  readonly watches: Readonly<Record<string, NativeBackupUploadWatch>>;
};

export const initialNativeBackupUploadGate: NativeBackupUploadGate = {
  channelArmed: false,
  watches: {},
};

function withWatch(
  gate: NativeBackupUploadGate,
  sessionId: string,
  next: NativeBackupUploadWatch | undefined
): NativeBackupUploadGate {
  const watches = { ...gate.watches };
  if (next === undefined) {
    delete watches[sessionId];
  } else {
    watches[sessionId] = next;
  }
  return { ...gate, watches };
}

/**
 * Mirror the producer's arm state into the FE.
 *
 * Disarming DROPS every watch, raised ones included: with the reconcile off,
 * nothing will report a settle, so a gate held here could only ever be held
 * forever. Called from the same effect that calls
 * `recorderFmc.onlineBackupArmReconcile`, keyed on the same
 * `[enabled, isAuthenticated]`, so the two cannot drift.
 */
export function setNativeBackupSettleChannelArmed(
  gate: NativeBackupUploadGate,
  armed: boolean
): NativeBackupUploadGate {
  if (armed) {
    return gate.channelArmed ? gate : { ...gate, channelArmed: true };
  }
  // Identity when there is nothing to change: this runs on every re-render of
  // the arm effect's disarmed path, and a fresh object each time would churn
  // every subscriber of the atom.
  if (!gate.channelArmed && Object.keys(gate.watches).length === 0) return gate;
  return initialNativeBackupUploadGate;
}

/**
 * Open the watch. MUST be called synchronously before the `stopSession` IPC —
 * that is what makes the ordering argument above hold, because the native
 * upload this watch is about cannot exist yet.
 *
 * Unconditionally replaces any earlier entry for the session: a second stop of
 * the same session is a new upload, and the previous stop's verdict must not
 * decide it. This is the reset point that keeps `settled` from becoming a
 * permanent refusal.
 */
export function openNativeBackupUploadWatch(
  gate: NativeBackupUploadGate,
  sessionId: string
): NativeBackupUploadGate {
  if (!sessionId) return gate;
  return withWatch(gate, sessionId, 'watching');
}

/**
 * The durable recovery row for this session reached a settled status.
 *
 * Lowers a raised gate, and — this is the half that closes the timing race —
 * marks a still-`watching` entry so the raise that has not run yet is refused.
 * A session with no watch is ignored rather than recorded, so this never grows
 * without a stop to bound it.
 */
export function observeNativeBackupUploadSettled(
  gate: NativeBackupUploadGate,
  sessionId: string
): NativeBackupUploadGate {
  const current = gate.watches[sessionId];
  if (current === undefined || current === 'settled') return gate;
  return withWatch(gate, sessionId, current === 'raised' ? undefined : 'settled');
}

/**
 * Raise the gate for a stop that answered `backupPendingAsync`.
 *
 * A compare-and-set, not an assignment: it takes effect only from `watching`,
 * and only while the channel is armed. Refusal is not a degraded outcome — see
 * the module note on why raising in either refused case is strictly worse.
 */
export function raiseNativeBackupUploadGate(
  gate: NativeBackupUploadGate,
  sessionId: string
): NativeBackupUploadGate {
  if (!gate.channelArmed) return gate;
  if (gate.watches[sessionId] !== 'watching') return gate;
  return withWatch(gate, sessionId, 'raised');
}

/**
 * The stop finished without raising. Reaps `watching` / `settled` so the map
 * stays the size of the live stops; a `raised` entry is deliberately untouched,
 * because the upload it names outlives the stop that started it.
 */
export function closeNativeBackupUploadWatch(
  gate: NativeBackupUploadGate,
  sessionId: string
): NativeBackupUploadGate {
  const current = gate.watches[sessionId];
  if (current === undefined || current === 'raised') return gate;
  return withWatch(gate, sessionId, undefined);
}

/** The one read. `raised` alone gates; `watching` and `settled` never do. */
export function isNativeBackupUploadGateRaised(
  gate: NativeBackupUploadGate,
  sessionId: string | undefined
): boolean {
  if (!sessionId) return false;
  return gate.channelArmed && gate.watches[sessionId] === 'raised';
}
